import { createContext, useContext, useEffect, useState } from "react";
import type { Session } from "@supabase/supabase-js";
import { supabase } from "@/lib/supabase";

export interface AuthContextValue {
  session: Session | null;
  userRole: string | null;
  loading: boolean;
  sendMagicLink: (email: string) => Promise<void>;
  signInWithGoogle: () => Promise<void>;
  signOut: () => Promise<void>;
}

// eslint-disable-next-line react-refresh/only-export-components
export const AuthContext = createContext<AuthContextValue | null>(null);

async function fetchRole(token: string): Promise<string | null> {
  try {
    const res = await fetch(`${import.meta.env.VITE_API_BASE_URL}/profile`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) return null;
    const data = await res.json();
    return data.role ?? null;
  } catch {
    return null;
  }
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [userRole, setUserRole] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    supabase.auth.getSession().then(async ({ data }) => {
      setSession(data.session);
      if (data.session?.access_token) {
        setUserRole(await fetchRole(data.session.access_token));
      }
      setLoading(false);
    });

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (_event, session) => {
      setSession(session);
      if (session?.access_token) {
        setUserRole(await fetchRole(session.access_token));
      } else {
        setUserRole(null);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const sendMagicLink = async (email: string) => {
    const { error } = await supabase.auth.signInWithOtp({
      email,
      options: { shouldCreateUser: false },
    });
    if (error) throw new Error(error.message);
  };

  const signInWithGoogle = async () => {
    // redirectTo is where Supabase returns the user AFTER it finishes the OAuth
    // exchange with Google — i.e. back to this SPA, not Supabase's own callback.
    // Google itself redirects to the Supabase callback URL
    // (https://<ref>.supabase.co/auth/v1/callback), which is configured in the
    // Google Cloud Console "Authorized redirect URIs", not here. The supabase-js
    // client (implicit flow, detectSessionInUrl: true) parses the returned
    // tokens from the URL on load, so no app-side /auth/callback route is needed.
    // This origin must be on the Supabase Auth "Redirect URLs" allow-list.
    //
    // Config gotcha (see CLAUDE.md): Supabase pairs the OAuth client secret with
    // the FIRST id in the provider's comma-separated "Client IDs" list, so the
    // Web client must be listed first — otherwise web login fails with
    // redirect_uri_mismatch while mobile (signInWithIdToken) still works.
    const { error } = await supabase.auth.signInWithOAuth({
      provider: "google",
      options: { redirectTo: window.location.origin },
    });
    if (error) throw new Error(error.message);
  };

  const signOut = async () => {
    await supabase.auth.signOut();
  };

  return (
    <AuthContext.Provider
      value={{ session, userRole, loading, sendMagicLink, signInWithGoogle, signOut }}
    >
      {children}
    </AuthContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
